import { faOverleaf } from '@awesome.me/kit-9c96a1f097/icons/kit/custom'
import { faBars, faClose } from '@fortawesome/pro-regular-svg-icons'
import { faArrowUpRightFromSquare, faFileWord } from '@fortawesome/pro-solid-svg-icons'
import { useState } from 'react'
import { Link, NavLink } from 'react-router'

import { Icon, IconDefinition, Overlay } from '@writefull/ui'
import { clsx } from '@writefull/utils'

import { Page } from './domain/page'

interface MenuItemLinkProps {
  path: string
  name: string
  icon: IconDefinition
  inNewTab?: boolean
  onClick?: () => void
}

function MenuItemLink({
  path,
  name,
  icon,
  inNewTab = false,
  onClick = undefined,
}: MenuItemLinkProps) {
  return (
    <NavLink
      to={path}
      onClick={onClick}
      target={inNewTab ? '_blank' : undefined}
      className={({ isActive }) =>
        clsx(
          'group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md theme-transition',
          isActive
            ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-white'
            : 'text-gray-500 dark:text-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-slate-700/50 dark:hover:text-white',
        )
      }
    >
      <Icon icon={icon} size={6} />
      <div className='flex-1'>{name}</div>
      {inNewTab && (
        <Icon
          icon={faArrowUpRightFromSquare}
          size={3}
          className={clsx(
            'text-gray-400 opacity-0 transition-opacity',
            'group-hover:opacity-100 group-focus:opacity-100',
          )}
        />
      )}
    </NavLink>
  )
}

interface MenuProps {
  pages: Page[]
}

export default function Menu({ pages }: MenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  const toggleMenu = () => setIsOpen(!isOpen)
  const closeMenu = () => setIsOpen(false)

  return (
    <>
      <Overlay isOpen={isOpen} onClose={closeMenu} zIndex={9} />
      <aside
        className={clsx(
          'fixed top-0 left-0 h-full w-72 theme shadow-lg z-[11]',
          'transform transition-all duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'md:w-full md:translate-x-0 md:static md:z-0 md:shadow-none',
          'md:border-r md:border-gray-200 dark:border-slate-600',
        )}
      >
        <button
          onClick={toggleMenu}
          className='absolute top-0 right-0 translate-x-full z-50 pt-3 pb-2 px-3 md:hidden theme theme-transition'
          aria-label={'Open menu'}
        >
          <Icon icon={isOpen ? faClose : faBars} size={6} />
        </button>
        <nav className='flex flex-col gap-8 py-6 px-2'>
          <Link to='/' onClick={closeMenu} className='self-center'>
            <img
              className={clsx('h-8 block dark:hidden')}
              src='/writefull-logo-black.png'
              alt='Writefull'
            />
            <img
              className={clsx('h-8 hidden dark:block')}
              src='/writefull-logo-white.png'
              alt='Writefull'
            />
          </Link>
          <ul className='flex flex-col gap-2'>
            {pages.map((page) => (
              <li key={page.name}>
                <MenuItemLink
                  path={page.path}
                  onClick={closeMenu}
                  icon={page.icon}
                  name={page.name}
                />
              </li>
            ))}
            <li>
              <MenuItemLink
                path='https://www.writefull.com/writefull-for-overleaf'
                icon={faOverleaf}
                name='Writefull for Overleaf'
                inNewTab
              />
            </li>
            <li>
              <MenuItemLink
                path='https://www.writefull.com/writefull-for-word'
                icon={faFileWord}
                name='Writefull for Word'
                inNewTab
              />
            </li>
          </ul>
        </nav>
      </aside>
    </>
  )
}
